/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';

import styles from './Divisions.module.scss';
import AddDivision from './add/AddDivision';
import { DivisionsListingResponseModel } from '../../models/divisions/DivisionsListingResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { Routes } from '../../routes';
import { nameof } from '../../shared/utility';

const Divisions = (): JSX.Element => {
	const [divisionsListing, setDivisionsListing] = useState<
		DivisionsListingResponseModel[]
	>([]);

	const [loading, setLoading] = useState<boolean>(false);

	const navigate = useNavigate();

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchDivisions = () => {
		setLoading(true);

		httpService
			.get<DivisionsListingResponseModel[]>('divisions', undefined)
			.then((divisions) => {
				setDivisionsListing(divisions);
				setLoading(false);
			})
			.catch((err) => notifierService.error());
	};

	useEffect(() => {
		fetchDivisions();
	}, [httpService, notifierService]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		propName: keyof DivisionsListingResponseModel
	) => {
		const divisionId: number = +e.target.id;
		const divisionIndex: number = divisionsListing.findIndex(
			(d) => d.id === divisionId
		);

		const division = divisionsListing[divisionIndex];

		(division as any)[propName] = e.target.value;

		try {
			await httpService.put(
				`divisions/${divisionId}/changeActive`,
				undefined,
				undefined,
				false
			);

			const divisions = await httpService.get<
				DivisionsListingResponseModel[]
			>('divisions', undefined);

			setDivisionsListing(divisions);
		} catch {
			notifierService.error();
		}
	};

	const onRenderActive = useCallback(
		(cellData: DivisionsListingResponseModel) => {
			return (
				<InputSwitch
					id={cellData.id.toString()}
					checked={cellData.active}
					onChange={(e) => handleInputSwitchChange(e, 'active')}
				/>
			);
		},
		[handleInputSwitchChange]
	);

	const onRenderNotificationsEnabled = (
		cellData: DivisionsListingResponseModel
	) => {
		return (
			<Checkbox
				disabled
				id={cellData.id.toString()}
				checked={cellData.notificationsEnabled}
			/>
		);
	};

	const onRenderEditButton = (cellData: DivisionsListingResponseModel) => {
		return (
			<i
				className={`${styles.editIcon} pi pi-pencil`}
				onClick={() =>
					navigate(
						Routes.editDivisions.getPath(cellData.id.toString())
					)
				}
			></i>
		);
	};

	return (
		<>
			<div className={styles.flexContainer}>
				<AddDivision />
			</div>

			<DataTable
				value={divisionsListing}
				responsiveLayout='scroll'
				loading={loading}
			>
				<Column
					style={{ width: '10%' }}
					sortable
					header='Status'
					body={onRenderActive}
					field={nameof<DivisionsListingResponseModel>('active')}
				/>

				<Column
					style={{ width: '16%' }}
					sortable
					header='Division code'
					field={nameof<DivisionsListingResponseModel>('code')}
				/>

				<Column
					style={{ width: '16%' }}
					sortable
					header='Division description'
					field={nameof<DivisionsListingResponseModel>('description')}
				/>

				<Column
					style={{ width: '16%' }}
					sortable
					header='Product type'
					field={nameof<DivisionsListingResponseModel>(
						'inputProductTypeDescription'
					)}
				/>

				<Column
					style={{ width: '16%' }}
					sortable
					header='Notifications Enabled'
					body={onRenderNotificationsEnabled}
					field={nameof<DivisionsListingResponseModel>(
						'notificationsEnabled'
					)}
				/>

				<Column body={onRenderEditButton} />
			</DataTable>
		</>
	);
};

export default Divisions;
